/**
 * ARCHITECTURE NOTE – TvdSidenavbarComponent Responsibilities (Cost Assumptions)
 *
 * This component acts as the STATE OWNER and ORCHESTRATION LAYER
 * for the Cost Assumptions feature.
 *
 *
 * ─── CURRENT STATE ───────────────────────────────────────────────────────────
 *
 * RESPONSIBILITIES:
 *   - Owns and prepares all state required for cost assumption workflows:
 *       metricsConfig         → defines which metrics are included
 *       baseMetricsDisplayConfig → filtered/grouped base view for rendering
 *       drilldownDetails      → TVD metric drilldown context
 *       previewState          → TvdDialogInputState contract passed to dialog
 *
 *   - Executes the save workflow via a single entry point:
 *       saveCostAssumptionsMetrics()
 *         → CostAssumptionsOrchestrator.applyCostAssumptions()
 *             → CostAssumptionsLayoutResolver  (determines flat vs byCountry payload)
 *             → MetricCostAssumptionsPayloadBuilder  (constructs API payload)
 *             → TVDService.refreshTVDDataWithAssumptions()  (API call)
 *
 *   - Executes the preview workflow via:
 *       previewInstitutionalTVDData() / previewPestTVDData()
 *         → CostAssumptionsOrchestrator.previewCostAssumptions()
 *         → buildPreviewState()  (constructs TvdDialogInputState for dialog)
 *
 *   - Passes dialog state and listens to dialog events:
 *       [state]="previewState"  (input)
 *       (save)="saveCostAssumptionsMetrics()"  (output)
 *
 * DATA FLOW:
 *   TvdSidenavbarComponent
 *        ├─ owns form and display state
 *        ├─ builds previewState → TvdDialogComponent (presentation only)
 *        ├─ listens for save event from dialog
 *        └─ delegates execution to CostAssumptionsOrchestrator
 *                ↓
 *   CostAssumptionsOrchestrator
 *        ├─ CostAssumptionsLayoutResolver   (flat vs byCountry)
 *        ├─ MetricCostAssumptionsPayloadBuilder
 *        └─ TVDService
 *
 * NOTE ON LAYOUT AND PAYLOAD:
 *   The API payload layout (flat vs byCountry) is already fully resolved by
 *   CostAssumptionsOrchestrator internally via CostAssumptionsLayoutResolver.
 *   No layout-specific logic is required in this component for the save/preview
 *   pipeline — it is layout-agnostic from an orchestration perspective.
 *
 *
 * ─── PENDING: byCountryMetrics TEMPLATE LAYOUT ───────────────────────────────
 *
 * The template currently only renders the flatMetrics layout (all metric groups
 * displayed sequentially). The byCountryMetrics layout is not yet implemented.
 *
 * To support byCountryMetrics rendering, the following changes are needed:
 *
 * 1. Expose the resolved layout type to the template:
 *      layout: CostAssumptionsLayout = 'flat';
 *    Set this in ngOnInit by calling:
 *      this.layout = this._layoutResolver.resolve();
 *    (inject CostAssumptionsLayoutResolver)
 *
 * 2. Build a country-grouped display config for byCountry rendering:
 *      countriesDisplayConfig: CountryMetricGroup[]
 *    ✅ Done — populated in setMetricsDisplayConfig() when layout === 'byCountry'.
 *    Each CountryMetricGroup contains a country-scoped metricsDisplayConfig copy.
 *
 * 3. Update the template to branch on the layout:
 *      <ng-container *ngIf="layout === 'flat'">
 *        → existing flat renderer
 *      </ng-container>
 *      <ng-container *ngIf="layout === 'byCountry'">
 *        → new country-grouped renderer (expandable sections per country)
 *      </ng-container>
 *
 * 4. Update initializeForm() to build nested FormGroups per country when
 *    layout === 'byCountry', so modifierForm.value produces:
 *      { JPN: { productivity: '1.5', energy: '2.0' }, KOR: { ... } }
 *    ✅ Done — initializeForm() now delegates to TvdCostAssumptionsFormBuilder,
 *    which builds either flat or country-scoped FormGroups based on layout.
 *
 *
 * ─── KEY PRINCIPLES ──────────────────────────────────────────────────────────
 *
 *   - This component is the only place that orchestrates saving.
 *     Dialog must NOT call services directly.
 *
 *   - Business logic is delegated to CostAssumptionsOrchestrator.
 *
 *   - TvdDialogComponent is strictly a presentation layer:
 *     receives state via @Input, emits events via @Output.
 *
 *   - Layout (flat vs byCountry) affects only the template rendering.
 *     The save and preview pipelines are layout-agnostic.
 */

import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TvdNavbarMetricsConfig } from '../../types/tvd-navbar-metrics-config';
import { eroiIds, Metric, MetricConfig, ModalActions, ModifiedFormData, PestTVDMetricsData, tvdData, PestMetricIds, TVDLocationDetails, TvdMetricData, TVDMetricGroupResponse, TVDProductDetails, TVDRateDetailsResponse,componentConfigAndMetricsIdsMap, TvdLocationServicesDetails } from '../../types/tvd-types';
import { EroiMetricComponent } from '../eroi-metric/eroi-metric.component';
import { TVDService } from '../../services/tvd.service';
import { TvdCostAssumptionsService } from '../../services/tvd-cost-assumptions.service';
import { TVDModel } from '../../types/tvd-model';
import { cloneDeep, forEach } from 'lodash';
import { combineLatest, skip, Subscription } from 'rxjs';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { FiltersService } from 'src/app/core/services/filters.service';
import { PestTvdNavbarMetricsConfig } from '../../types/pest-tvd-navbar-metrics-config';
import { QsrTvdNavbarMetricsConfig, FrsTvdNavbarMetricsConfig, EuTvdNavbarMetricsConfig, APLAIMEATvdNavbarMetricsConfig } from '../../types/generic-metric-configs.builder';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { CapitalizePipe } from '../../../../core/pipes/capitalize.pipe';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';
import { CurrencySymbolPipe } from 'src/app/core/pipes/currency-symbol.pipe';
import { MetricCostAssumptions, FlatMetricCostAssumptions } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { CostAssumptionsOrchestrator } from '../../orchestrators/cost-assumptions.orchestrator';
import { CountryMetricGroup, TvdDialogInputState } from '../../types/tvd-contract-types';
import { CostAssumptionsLayout } from '../../types/tvd-types';
import { CostAssumptionsLayoutResolver } from '../../resolvers/cost-assumptions-layout.resolver';
import { CountriesService } from 'src/app/core/services/countries.service';
import { TvdCostAssumptionsFormBuilder } from '../../builders/tvd-cost-assumptions-form.builder';

@Component({
    selector: 'app-tvd-sidenavbar',
    templateUrl: './tvd-sidenavbar.component.html',
    styleUrls: ['./tvd-sidenavbar.component.scss'],
    providers: [CapitalizePipe],
    standalone: false
})
export class TvdSidenavbarComponent {

  @Input() isOpen = false;
  @Input() drilldownComponentTitle: string | undefined;
  @Input() isInDrillDown: boolean = false;
  @Input() isInMetric: boolean = false;
  @Input() interactionType: string = "All Mertics";

  @Output() closeSideDrawer = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<void>();

  filters: ContractModel | undefined;
  filtersSubscription: Subscription | undefined;

  private currencySubscription: Subscription | undefined;

  modifierForm: FormGroup;

  isPreview = false;
  isLoading = false;
  metricsConfig!: MetricConfig[]; // = TvdNavbarMetricsConfig;
  /**
   * Shared, filtered display config used as the base source for UI rendering.
   * - Flat layout renders this collection directly.
   * - By-country layout clones this into each country section.
   */
  baseMetricsDisplayConfig!: MetricConfig[];

  showWarning: boolean = false;
  hasValidMetricInput: boolean = false;
  overallTVDModelSelections : TVDMetricGroupResponse = {id: '', brandGroup: '', indvMetrics: [], summMetrics: []};
  tvdData: TVDModel | undefined;
  isAPIDataUpdated = false;
  selectedDivision:string = '';
  tvdRateDetailsResponse:TVDRateDetailsResponse | undefined;

  // Drilldown TVD data
  tvdProductDetailsData: TVDProductDetails | undefined = undefined;
  tvdLocationDetailsData: TVDLocationDetails | undefined = undefined;

  tvdLocationServicesData: TvdLocationServicesDetails | undefined = undefined;


 
  //PEST TVD DATA
  pestTVDMetricsData: PestTVDMetricsData | undefined;

  selectedProgram: string = "";
  isPestDivision: boolean = false;
  configMetricId:string[] = [];

  hasAdjustableMetrics: boolean = false;

  pestMetrics= PestMetricIds;
  pestComponents = [
    { key: 'furniturePreservationAssetsComponent', id: eroiIds.FURNITUREPRESERVATIONASSETS },
    { key: 'furniturePreservationEnergyComponent', id: eroiIds.FURNITUREPRESERVATIONENERGY },
    { key: 'noChargeAdditionalServicesComponent', id: eroiIds.NOCHARGEADDITIONALSERVICES },
    { key: 'generalContractorCostAvoidanceComponent', id: eroiIds.GENERALCONTRACTORCOSTAVOIDANCE },
    { key: 'setupServicesComponent', id: eroiIds.SETUPSERVICES },
    { key: 'pestPreventionFindingsComponent', id: eroiIds.PESTPREVENTIONFINDINGS },
    { key: 'roomNightsSavedComponent', id: eroiIds.ROOMNIGHTSSAVED },
  ];

  componentConfigAndMetricsIdMap = componentConfigAndMetricsIdsMap;
  isScalingFactor = 'scaling factor';

  private readonly divisionConfigMap = new Map([
    ['pest', PestTvdNavbarMetricsConfig],
    ['qsr', QsrTvdNavbarMetricsConfig],
    ['frs', FrsTvdNavbarMetricsConfig],
    ['eu', EuTvdNavbarMetricsConfig],
    ['ap', APLAIMEATvdNavbarMetricsConfig],
    ['la', APLAIMEATvdNavbarMetricsConfig],
    ['imea', APLAIMEATvdNavbarMetricsConfig]
  ]);

  previewState: TvdDialogInputState = this.buildEmptyPreviewState();

  previewTvdModel?: TVDModel;

  /**
   * Builds the empty, preview-free dialog state used before a preview is
   * generated and after a preview is discarded.
   */
  private buildEmptyPreviewState(): TvdDialogInputState {
    return {
      previewData: {
        isPestDivision: false,
        tvdProgramScope: ''
      },
      isDrillDownModal: false
    };
  }

  /**
   * Active layout type — resolved from CostAssumptionsLayoutResolver on init.
   */
  layout: CostAssumptionsLayout = 'flat';

  /**
   * Country sections for the byCountryMetrics accordion layout.
  * Populated in setMetricsDisplayConfig() when layout === 'byCountry'.
  * Each entry carries a country-scoped copy of baseMetricsDisplayConfig,
  * scoped to its own country code for per-country rate lookups.
   */
  countriesDisplayConfig: CountryMetricGroup[] = [];

  private formSubscription?: Subscription;

  constructor(private fb: FormBuilder,
    private _tvdService: TVDService,
    private _filterService: FiltersService,
    private _mixpanelService: MixpanelService,
    private _tvdCostAssumptionsService:TvdCostAssumptionsService,
    private _costAssumptionsOrchestrator: CostAssumptionsOrchestrator,
    private _costAssumptionsLayoutResolver: CostAssumptionsLayoutResolver,
    private _costAssumptionsFormBuilder: TvdCostAssumptionsFormBuilder,
    private _currencySymbolPipe: CurrencySymbolPipe,
    private _currencyUomService: CurrencyUomService,
    private _countriesService: CountriesService
  ) {
    this.modifierForm = this.fb.group({});
  }

  async ngOnInit() {

    this.isLoading = true;

    this.subscribeToFilters();
    this.layout = this._costAssumptionsLayoutResolver.resolve();

    if (this.filters?.division && this.filters.regions) {
      this.selectedDivision = this.filters?.division;

      this.isPestDivision = this.filters?.division?.toLowerCase() === 'pest';

      this.initializeDivisionConfig();

      this._tvdService.currentProgramSelectedObservable.subscribe((program) => {
        this.selectedProgram = program || '';
      });

     await this.fetchTvdRateDetails();
    }
    this.configMetricId = this.componentConfigAndMetricsIdMap.get(this.drilldownComponentTitle ?? '') || [];
    this.setMetricsDisplayConfig();
    this.initializeForm();

    // Subscribe to tvdObservable to get the latest data
    this._tvdService.tvdObservable.subscribe((tvdData: TVDModel | undefined) => {
      this.tvdData = tvdData;
    });

    this.loadTvdDataBasedOnDivision();

    this.handleFormValueChanges();
    //Get the TVD drilldown details data
    this.tvdLocationDetailsData = this._tvdService.getTvdLocationDetailsData();
    this.tvdProductDetailsData = this._tvdService.getTvdProductDetailsData();
    this.tvdLocationServicesData = this._tvdService.getPestTvdLocationDetailsData();

    this.loadSavedFormData();

    //As the display currency is updated 
    this.currencySubscription = combineLatest([
      this._currencyUomService.displayedCurrency$,
      this._currencyUomService.selectedUom$,
    ]).pipe(skip(1)).subscribe(async ([displayedCurrency, selectedUom]) => {
        // TODO: The logic in this subscription may be better suited to a shared service that
        // updates the cost assumption form values and refreshes the appropriate data.

        this._tvdService.setShowLoadingOverlay(true);

        this.isAPIDataUpdated = false;
        this.loadTvdDataBasedOnDivision();
        const previousFormData: ModifiedFormData = this._tvdService.getFormData();
        this.loadSavedFormData();
        // In case the subscription block is triggered multiple times, 
        // track currentCostAssumptionsCurrencyCode to avoid multiple unnecessary currency conversions
        let currentCostAssumptionsCurrencyCode = this._tvdCostAssumptionsService.getCostAssumptionsCurrencyCode();
        let currentCostAssumptionsUoMSystem = this._tvdCostAssumptionsService.getCostAssumptionsUnitOfMeasureSystem();

        if((displayedCurrency !== currentCostAssumptionsCurrencyCode)){
          // Take the user-inputted form data and convert their inputs to the new currency
          if (Object.keys(previousFormData).length) {
            let conversionRate = await this._currencyUomService.getCurrencyConversionRate();
            this.updateFormValues(
              previousFormData, 
              this.modifierForm, 
              value => value * conversionRate);
            this._tvdCostAssumptionsService.setCostAssumptionsCurrencyCode(displayedCurrency);
          }
          // Re-fetch the default tvd rate details from the API with the desired currency conversion
          await this.fetchTvdRateDetails();
          this._tvdService.setFormData(this.modifierForm.value); 
          this._tvdCostAssumptionsService.notifyCostAssumptionsFormValuesUpdated();
        }

        if(selectedUom !== currentCostAssumptionsUoMSystem)
        {
          // Take the user-inputted form data and convert their inputs to the new unit of measure
          if (Object.keys(previousFormData).length) {
            let uomConversionRates = await this._currencyUomService.getUnitOfMeasurementSystemConversionRates();
            this.updateFormValues(
              previousFormData,
              this.modifierForm,
              (value, key) => {
              const uomConversionRate = uomConversionRates?.conversionRates?.find(rate => rate.id === key)?.conversionFactor ?? 1;
              return value / uomConversionRate;
            });
            this._tvdCostAssumptionsService.setCostAssumptionsUnitOfMeasureSystem(selectedUom);
          }
          // Re-fetch the default tvd rate details from the API with the desired currency conversion
          await this.fetchTvdRateDetails();
          this._tvdService.setFormData(this.modifierForm.value); 
          this._tvdCostAssumptionsService.notifyCostAssumptionsFormValuesUpdated();
        }

        // Ensure updated form values are set
        this._tvdService.setFormData(this.modifierForm.value); 
        this.loadSavedFormData();

        // Apply the newly updated cost assumptions to the TVD details data now that they are both using the desired currency
        this.tvdLocationDetailsData = this._tvdService.getTvdLocationDetailsData();
        this.tvdProductDetailsData = this._tvdService.getTvdProductDetailsData();
        this.tvdLocationServicesData = this._tvdService.getPestTvdLocationDetailsData();

        // TVD details for INST, QSR, FRS will be updated in the tvd-details-page component.

        // PEST
        //TODO US595383: Check that Pest metrics are updated and saved correctly
        if(this.tvdLocationServicesData)
        {
          this.tvdLocationServicesData = await this._tvdCostAssumptionsService.savePestDrillDownTVDMetricData(
            this.tvdLocationServicesData?.metricType,//formattedComponentTitle,
            this.tvdLocationServicesData,
            this.modifierForm.value,
            true,
            this.metricsConfig
          );
        }        

        // Save metrics based on division type
        if (this.isPestDivision) {
          //TODO US595383: Check that Pest metrics are updated and saved correctly
           await this.savePestMetrics();
        } else {
          await this._costAssumptionsOrchestrator.applyCostAssumptions(this.modifierForm.value);
        }
  
        this._tvdService.setShowLoadingOverlay(false);
    });
    
    this._tvdCostAssumptionsService.setCostAssumptionsCurrencyCode(this._currencyUomService.getCurrency());
    this._tvdCostAssumptionsService.setCostAssumptionsUnitOfMeasureSystem(this._currencyUomService.getUom());

  }

  //Get the TVD data from the TVD Main page when we click on Cost Assumption Buttom from TVD Main page.
  /**
   * TODO (Orchestrator Refactor):
   * This method is currently used by the currency-change workflow
   * to update component state after cost assumption values are
   * converted between currencies.
   *
   * Evaluate whether this workflow should be moved into a dedicated
   * CurrencyRefreshOrchestrator so that component-specific update
   * logic can be removed from TvdSidenavbarComponent.
   */
  loadTvdDataBasedOnDivision() {
    if (this.isPestDivision) {
      this.getPestTVDMetricsData();
    } 
  }

  getComponentIdsBasedOnDrilldownComponentTitle(){
    switch (this.drilldownComponentTitle?.toLowerCase()) {
      case eroiIds.NOCHARGEADDITIONALSERVICES:
      case eroiIds.SETUPSERVICES:
        return [eroiIds.NOCHARGEADDITIONALSERVICES,eroiIds.SETUPSERVICES];
      default:
        return [this.drilldownComponentTitle ?? ''];
    }
  }

  getPestTVDMetricsData(){
    if (this.isInMetric && this.drilldownComponentTitle !== undefined) {
      const componentIds = this.getComponentIdsBasedOnDrilldownComponentTitle();
      forEach(componentIds, (componentId) => {
        const component = this.pestComponents.find(({ id }) => id === componentId);
        if (component) {
          this.pestTVDMetricsData = {
            ...this.pestTVDMetricsData, // Preserve existing values
            [component.key]: this._tvdService.getCostAssumptionsComponentData(component.id)
          } as PestTVDMetricsData;
        }
      });
    }else{
      this.pestTVDMetricsData = this.pestComponents.reduce((acc, { key, id }) => {
        acc[key as keyof PestTVDMetricsData] = this._tvdService.getCostAssumptionsComponentData(id);
        return acc;
       }, {} as PestTVDMetricsData);
    }
  }

  initializeForm(): void {
    this.modifierForm = this._costAssumptionsFormBuilder.build({
      layout: this.layout,
      metricsConfig: this.metricsConfig,
      countriesDisplayConfig: this.countriesDisplayConfig
    });

    // Cleanup previous subscription
    this.formSubscription?.unsubscribe();

    // Re-subscribe
    this.formSubscription = this.modifierForm.valueChanges.subscribe((val) => {
      this.hasValidMetricInput = this.computeHasValidMetricInput();
      this.showWarning = false;
    });

    this.hasValidMetricInput = this.computeHasValidMetricInput();
  }

  /***
   * If adjusting cost assumptions via the tvd drilldown page, only display the relevant metrics.
   * Uses configMetricId (from componentConfigAndMetricsIdsMap) to dynamically filter metrics
   * by matching against both parent metric IDs and twoFactorMetric IDs — no hardcoded cases needed.
   */
  setMetricsDisplayConfig(): void {
    this.baseMetricsDisplayConfig = cloneDeep(this.metricsConfig);

    if (this.drilldownComponentTitle !== undefined && this.configMetricId.length > 0) {
      this.baseMetricsDisplayConfig = this.baseMetricsDisplayConfig
        .map(group => ({
          ...group,
          metrics: group.metrics.filter(metric => this.isMetricRelevantForDrilldown(metric))
        }))
        .filter(group => group.metrics.length > 0);

      // Keep metricsConfig in sync — form init and payload logic depend on it
      this.metricsConfig = cloneDeep(this.baseMetricsDisplayConfig);
    }

    // Add hasVisibleMetrics property to each metricConfig if all metrics inside a metricConfig are hidden
    this.baseMetricsDisplayConfig.forEach(metricConfig => {
      metricConfig.hasVisibleMetrics = metricConfig.metrics.some(metric => !metric.isHidden);
    });

    if (this.layout === 'byCountry') {
      this.countriesDisplayConfig = (this.filters?.countries ?? []).map(countryCode => ({
        countryCode,
        countryName: this._countriesService.getCountryName(countryCode),
        expanded: true,
        metricsDisplayConfig: cloneDeep(this.baseMetricsDisplayConfig)
      }));
    }

    this.hasAdjustableMetrics = this.hasAnyVisibleMetrics();
  }

  /**
   * Returns true if a metric should be shown for the current drilldown context.
   * For simple metrics: checks if the parent metric id is in configMetricId.
   * For two-factor metrics: ALL configMetricIds must be present in the metric's twoFactorMetrics.
   * Using `every` (not `some`) prevents metrics that share a common twoFactorMetric id
   * (e.g. 'valueofservice' is shared by both No Charge Additional Services and Set-up Services)
   * from incorrectly appearing in each other's drilldown view.
   */
  private isMetricRelevantForDrilldown(metric: Metric): boolean {
    if (this.configMetricId.includes(metric.id)) {
      return true;
    }
    if (metric.isTwoFactorsApplicable && metric.twoFactorMetrics) {
      const twoFactorIds = metric.twoFactorMetrics.map(tf => tf.id ?? '');
      return this.configMetricId.every(id => twoFactorIds.includes(id));
    }
    return false;
  }


  hasAnyVisibleMetrics(): boolean {
    return this.baseMetricsDisplayConfig?.some(config => config.hasVisibleMetrics) || false;
  }

  closeSideNavBar() {
    this.resetForm();
    this.closeSideDrawer.emit();
  }

  closeModalPopUp(action:string) {
    this.isAPIDataUpdated = false;
    this.isPreview = false;

    // Preview data is throw-away: discard it so a cancelled preview can never
    // be reused or leak into the applied TVD state.
    this.previewState = this.buildEmptyPreviewState();
    this.previewTvdModel = undefined;

    if(ModalActions.SAVE === action){
      this.resetForm();
      this.closeSideNavBar();
    }
  }


  /**
   * Saves the form data if one of the text boxes is not empty.
   * If any text box is empty, it sets the showWarning flag to true.
   * Otherwise, it sets the showWarning flag to false and closes the side navigation bar.
   */
  async saveCostAssumptionsMetrics() {

    if (!this.hasValidMetricInput) {
      this.showWarning = true;
      return;
    }

    this.isLoading = true;
    this.showWarning = false;

    try {
      await this._costAssumptionsOrchestrator.applyCostAssumptions(this.modifierForm.value);

      // Push metrics to mixpanel
      const interactionType = this._tvdCostAssumptionsService.getInteractionContext().type;
      const metricName = this._tvdCostAssumptionsService.getInteractionContext().metricName;
      this._mixpanelService.trackEventWithFilters('Cost Adjustment Saved', this.filters, "Total Value Delivery", {
        'Interaction Type': interactionType,
        'Metric Name': metricName,
        ...this._tvdCostAssumptionsService.buildMetricsProperties(this.tvdRateDetailsResponse)
      });

      this._tvdService.setTvdModalButtonAction(ModalActions.SAVE);
      if (this.isInDrillDown) {
        await this.saveDrillDownData();
      }
      this.onSave?.emit();
      this.closeSideNavBar();

    } catch (error) {
      console.error('Error saving cost assumptions metrics:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle drill-down specific save logic
  */
  private async saveDrillDownData(): Promise<void> {
    if (this.isPestDivision) {
      await this.savePestDrillDownTVDMetricData();
    } else {
      await this.saveNonPestDrillDownTVDMetricData();
    }
  }

  /**
   * Save pest division metrics
  */
  /**
   * TODO (Orchestrator Refactor):
   * This method is currently used by the currency-change workflow
   * to update component state after cost assumption values are
   * converted between currencies.
   *
   * Evaluate whether this workflow should be moved into a dedicated
   * CurrencyRefreshOrchestrator so that component-specific update
   * logic can be removed from TvdSidenavbarComponent.
   */
  private async savePestMetrics(): Promise<void> {
    await this.updatePestComponentData(ModalActions.SAVE);

    // Process each pest metric using service (the service already checks for valid contributions)
    const pestMetrics = [
      eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,
      eroiIds.PESTPREVENTIONFINDINGS,
      eroiIds.FURNITUREPRESERVATIONASSETS,
      eroiIds.ROOMNIGHTSSAVED,
      eroiIds.NOCHARGEADDITIONALSERVICES,
      eroiIds.SETUPSERVICES,
      eroiIds.FURNITUREPRESERVATIONENERGY
    ];

    pestMetrics.forEach(metricId => {
      this._tvdCostAssumptionsService.updateMetricDataInSubject(metricId, this.tvdData);
    });

    await this._tvdCostAssumptionsService.updateTotalTVDData(this.tvdData, true);
  }


  //region Non-Pest Save Methods
  /**
   * Saves the institutional drill-down TVD metric data based on the current drill-down state.
   * Handles both productivity and non-productivity metrics, updating product and location details.
   * Performs API calls for productivity metrics and calculates estimated metric dollars for others.
   * Updates the modified average TVD metric quantities accordingly.
   */
  async saveNonPestDrillDownTVDMetricData() {
    await this._tvdCostAssumptionsService.saveNonPestDrillDownTVDMetricData(
      this.drilldownComponentTitle,
      this.modifierForm.value,
      this.isInDrillDown,
      this.selectedProgram,
      this.tvdLocationDetailsData,
      this.tvdProductDetailsData
    );
  }

  /**
 * Saves the pest drill-down TVD metric data based on the current drill-down state.
 * Updates location services details using the modified cost assumptions.
 */
  async savePestDrillDownTVDMetricData() {
    const formattedComponentTitle = this.drilldownComponentTitle?.replace(/[()\-]/g, '').split(" ").join("_").toLowerCase();

    this.tvdLocationServicesData = await this._tvdCostAssumptionsService.savePestDrillDownTVDMetricData(
      formattedComponentTitle,
      this.tvdLocationServicesData,
      this.modifierForm.value,
      this.isInDrillDown,
      this.metricsConfig
    );
  }

  /**
   * Opens a dialog if not all text boxes are empty.
   *
   * - If any text box is not empty:
   *   - Hides the warning message.
   *   - Sets the loading state to true.
   *   - Updates the component data for energy and water.
   *   - Sets the loading state to false.
   *   - Opens the dialog.
   * - If all text boxes are empty:
   *   - Shows the warning message.
   */
  async previewComponentData() {
    this.showWarning = !this.hasValidMetricInput;

    // STOP execution if empty
    if (!this.hasValidMetricInput) {
      return;
    }

    try {
      this.isLoading = true;

      if (this.isPestDivision) {
        await this.previewPestTVDData();
      } else {
        await this.previewInstitutionalTVDData();
      }

      this.isPreview = true;

    } finally {
      this.isLoading = false;
    }
  }

  //region Preview Methods
  /**
   * Previews the institutional TVD data by fetching and updating component data.
   * Handles loading state, errors, and modal actions.
   */
  async previewInstitutionalTVDData(){
    this.showWarning = false;
    this.isLoading = true;
    // NOTE: Preview must not persist form data. Only saveCostAssumptionsMetrics() commits.
    try {
      this.previewTvdModel = await this._costAssumptionsOrchestrator.previewCostAssumptions(this.modifierForm.value);
      this.previewState = this.buildPreviewState(this.previewTvdModel);
    } catch (error) {
      console.error('Error fetching productivity metric data:');
    } finally {
      this.isLoading = false;
      this.isPreview = true;
      this._tvdService.setTvdModalButtonAction(ModalActions.PREVIEW);
    }
  }

  /**
   * Previews pest TVD data by updating component data and fetching metrics,
   * while managing loading state and modal actions.
   */
  async previewPestTVDData(){
    this.showWarning = false;
    this.isLoading = true;
    // NOTE: Preview must not persist form data. Only saveCostAssumptionsMetrics() commits.
    try{
      this.previewTvdModel = await this._costAssumptionsOrchestrator.previewCostAssumptions(this.modifierForm.value);
      this.previewState = this.buildPreviewState(this.previewTvdModel);
    }catch (error) {
      console.error('Error fetching pest metric data:');
    }
    finally {
      this.isLoading = false;
      this.isPreview = true;
      this._tvdService.setTvdModalButtonAction(ModalActions.PREVIEW);
    }
  }

  /**
   * Returns the default rate for a metric.
   *
   * @param metricId   The metric ID to look up.
   * @param country    Optional country code for byCountryMetrics layout.
   *                   When provided, delegates to TVDService.getDefaultRate() which
   *                   performs a country-scoped rate lookup from tvdRateDetails.
   * @param isDecimal  When false, returns a parsed float instead of a string.
   */
  getDefaultRate(metricId: string, country?: string | null, isDecimal: boolean = true): string | number {
    if (country) {
      return this._tvdService.getDefaultRate(metricId, country, isDecimal);
    }
    const rate = (this.tvdRateDetailsResponse?.tvdRateDetails?.find(x => x.id === metricId)?.rate ?? 0).toFixed(2);
    return isDecimal ? rate : parseFloat(rate);
  }

  /**
   * Toggles the expanded state of a country accordion section.
   * TODO (byCountryMetrics): this is currently a placeholder.
   * When fully implemented, collapsing one country may optionally
   * collapse others (accordion behaviour) depending on UX requirements.
   */
  toggleCountry(country: CountryMetricGroup): void {
    country.expanded = !country.expanded;
  }

  getUOM(metricId:string){
    const isMetric = this._currencyUomService.getUom() === 'Metric';
    const currency = this._currencySymbolPipe.transform(this._currencyUomService.getCurrencyCode());

    const unitMap: Record<string, { imperial: string; metric: string }> = {
      'water': { imperial: '1000 gallons', metric: '1000 liters' },
      'energy': { imperial: 'therm', metric: 'kWh' },
      'hospitality': { imperial: 'hour', metric: 'hour' },
      'foodservice': { imperial: 'hour', metric: 'hour' },
      'productivity': { imperial: 'hour', metric: 'hour'}
    };

    const mapping = unitMap[metricId];
    if (mapping) {
      const unit = isMetric ? mapping.metric : mapping.imperial;
      return `(${currency}/${unit})`;
    }

    //This section will get the UoM for Pest division cost assumptions.
    const UOM = this.tvdRateDetailsResponse?.tvdRateDetails?.find(x => x.id === metricId)?.uom ?? '';
    return UOM !== this.isScalingFactor ? `(${UOM})` : '';
  }


  /**
   * Fetches and processes PEST metrics data, updating the corresponding components with calculated values.
   * Sets a flag to indicate that productivity metric data has been updated.
  */
  async getPestMetricsData(actionType : string) {
    if (!this.isAnyPestMetricValid()) {
      return;
    }
    const pestCostAssumptionsMetricPayload = this._tvdCostAssumptionsService.getPestPayloadFromFormData(this.modifierForm, this.metricsConfig);

    const tvdResponse: TVDMetricGroupResponse[] = await this._tvdService.getTVDData(pestCostAssumptionsMetricPayload);

    if (tvdResponse) {
      const indvMetricsData = this.extractIndividualMetrics(tvdResponse);
      indvMetricsData.forEach(metric => this.processMetric(metric, pestCostAssumptionsMetricPayload));
      this.isAPIDataUpdated = true;
    }

    if(actionType == ModalActions.SAVE) {
      this._tvdService.updateModifiedMonetaryContributions(eroiIds.NOCHARGEADDITIONALSERVICES, this.pestTVDMetricsData?.noChargeAdditionalServicesComponent?.data?.monetaryContribution || '');
      this._tvdService.updateModifiedMonetaryContributions(eroiIds.FURNITUREPRESERVATIONENERGY, this.pestTVDMetricsData?.furniturePreservationEnergyComponent?.data?.monetaryContribution || '');
      this._tvdService.updateModifiedMonetaryContributions(eroiIds.SETUPSERVICES, this.pestTVDMetricsData?.setupServicesComponent?.data?.monetaryContribution || '');
    }
  }

  private extractIndividualMetrics(tvdResponse: TVDMetricGroupResponse[]): TvdMetricData[] {
    return JSON.parse(
      JSON.stringify(
        tvdResponse.find(group => group.id === "pest_costassumptionsmetrics")?.indvMetrics || []
      )
    );
  }

  private processMetric(metric: TvdMetricData, payload: MetricCostAssumptions): void {
    const pestMapMetricsValues = this.componentConfigAndMetricsIdMap.get(metric.id);
    const component = this.getPestComponentById(metric.id);

    if (this.isMetricValidForUpdatePestComponent(pestMapMetricsValues, payload)) {
      this.updateComponentWithMetricData(component, metric);
    } else {
      this.resetComponentData(component, metric.id);
    }
  }

  private isAnyPestMetricValid(): boolean {
    return (
      this.isMetricValid(this.pestTVDMetricsData?.noChargeAdditionalServicesComponent?.data, 'valueofservice') ||
      this.isMetricValid(this.pestTVDMetricsData?.setupServicesComponent?.data, 'valueofservice') ||
      this.isMetricValid(this.pestTVDMetricsData?.setupServicesComponent?.data, 'setupservicescomultiplier') ||
      this.isMetricValid(this.pestTVDMetricsData?.furniturePreservationEnergyComponent?.data, 'furniturepreservationenergy')
    );
  }

  private isMetricValidForUpdatePestComponent(
    pestMapMetricsValues: string[] | undefined,
    payload: MetricCostAssumptions
  ): boolean {
    return pestMapMetricsValues?.some(key => payload.hasOwnProperty(key)) ?? false;
  }

  private updateComponentWithMetricData(component: EroiMetricComponent | undefined, metric: TvdMetricData): void {
    if (component?.data) {
      const monetaryContribution = this._tvdService.getMonetaryContributionValue(metric.id);
      component.data = {
        monetaryContribution: metric.modifiedMonetaryContribution,
        valueContribution: metric.valueContribution,
        modifiedMonetaryContribution: monetaryContribution,
        modifiedValueContribution: metric.modifiedValueContribution
      };
    }
  }

  private resetComponentData(component: EroiMetricComponent | undefined, metricId: string): void {
    if (component?.data) {
      component.data = this._tvdService.getCostAssumptionsComponentData(metricId)?.data;
    }
  }

  private isMetricValid(componentData: tvdData | undefined, formControlName: string): boolean {
    const formControlValue = this.modifierForm.value[formControlName];
    return this.hasValidMetricData(componentData, formControlValue);
  }

  /**
   * Returns true when any control in the form tree contains a positive numeric value.
   *
   * The check recurses through nested objects so the same logic works for both the
   * flat layout and the by-country layout, where each country owns its own FormGroup.
   */
  computeHasValidMetricInput(): boolean {
    const check = (val: any): boolean => {

      if (val === null || val === undefined) return false;

      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (!trimmed) return false;

        const num = Number(trimmed);
        return !isNaN(num) && num > 0;
      }

      if (typeof val === 'object') {
        return Object.values(val).some(check);
      }

      if (typeof val === 'number') {
        return !isNaN(val) && val > 0;
      }

      return false;
    };

    return check(this.modifierForm.getRawValue());
  }

  private isMetricEmpty(metric: any): boolean {
    if (metric.isTwoFactorsApplicable) {
      return (metric.twoFactorMetrics ?? []).every((twoFactorMetric: { id?: string }) =>
        !this.modifierForm.get(twoFactorMetric?.id ?? '')?.value
      );
    }
    return !this.modifierForm.get(metric.id)?.value;
  }

  private checkAllMetricsEmpty(metricsConfig: any[]): boolean {
    return metricsConfig.every(metricConfig =>
      metricConfig.metrics.every((metric: { id: string; isTwoFactorsApplicable?: boolean; twoFactorMetrics?: { id?: string }[] }) => this.isMetricEmpty(metric))
    );
  }


  /**
   * Converts the metrics from the form controls into a record object.
   *
   * This method filters the metrics based on the `isIncludePayload` property
   * and then maps the metric IDs to their corresponding values from the form controls.
   * Only numeric values are included in the resulting record.
   *
   * @returns {Record<string, number>} A record object where the keys are metric IDs and the values are the corresponding numeric values from the form controls.
   */
  convertMetricsToRecord(metricId:string): MetricCostAssumptions {

    const filteredIds = this.metricsConfig.filter(x=>x.metricSubHeader?.toLowerCase() === metricId?.toLowerCase()).flatMap(config =>
        config.metrics
            .filter(metric => metric.isIncludePayload)
            .map(metric => metric.id)
    );
    const metricCostAssumptions: FlatMetricCostAssumptions = new FlatMetricCostAssumptions();
    metricCostAssumptions.rates = metricCostAssumptions.rates || {};
    const formControls = this.modifierForm.controls;
    for (const key in formControls) {
      if (formControls.hasOwnProperty(key)) {
        const control = formControls[key];
        if (control && control.value) {
          const value = parseFloat(control.value);
          if (!isNaN(value) && filteredIds.includes(key)) {
            metricCostAssumptions.rates[key] = value;
          }
        }
      }
    }

    return metricCostAssumptions;
  }

  hasValidContribution(data:tvdData | undefined): boolean {
    if (!data)
      return false;

    const hasMonetaryContribution = data.monetaryContribution !== undefined &&
      data.monetaryContribution !== '';

    const hasValueContribution = data.valueContribution !== undefined &&
      data.valueContribution !== '';

    // Check if metric has zero monetary and value contribution, the metric should not be displayed at all.
    // But, monetaryContribution can be zero if valueContribution is non-zero and vice-versa.
    const hasTotalZeroContribution =
      data.monetaryContribution !== undefined && data.monetaryContribution == '0'  &&
      data.valueContribution !== undefined && data.valueContribution == '0';

    // Return true as long as either the monetaryContribution or valueContribution has a value.
    return (hasMonetaryContribution && hasValueContribution) && !hasTotalZeroContribution;
  }

  private hasValidMetricData(componentData: tvdData | undefined, formControlValue: number): boolean {
    return this.hasValidContribution(componentData) && formControlValue > 0;
  }

  /**
   * Resets the form to its initial state by clearing all input values.
   * This method is called when the modal is closed or after saving.
   */
  resetForm(): void {
    this.resetControlTree(this.modifierForm);

    // Reset warning flag
    this.showWarning = false;
  }

  /**
   * Recursively clears control values so reset works for both flat and by-country forms.
   */
  private resetControlTree(control: AbstractControl): void {
    if (control instanceof FormGroup) {
      Object.values(control.controls).forEach(child => this.resetControlTree(child));
      control.markAsPristine();
      control.markAsUntouched();
      return;
    }

    control.setValue('');
    control.markAsPristine();
    control.markAsUntouched();
  }

  /**
 * Filters the PestTvdNavbarMetricsConfig to find the twoFactorMetrics IDs for a given metricId.
 * @param metricId - The ID of the metric to filter.
 * @returns An array of twoFactorMetrics IDs if isTwoFactorsApplicable is true, otherwise an empty array.
 */
  private getTwoFactorMetricIdsByMetricId(metricId: string): string[] {
    for (const config of PestTvdNavbarMetricsConfig) {
      const metric = config.metrics.find((m) => m.id === metricId && m.isTwoFactorsApplicable);
      if (metric && metric.twoFactorMetrics) {
        return metric.twoFactorMetrics
          .map((twoFactorMetric) => twoFactorMetric.id)
          .filter((id): id is string => id !== undefined);
      }
    }
    return [];
  }

  private async updatePestComponentData(actionType: string) {
    // Second multiplier is only used for calcualtion of Furniture Preservation Assets metric.
    const furnitureAssetsMultiplier = Number(this.getDefaultRate("furniturepreservationassetsmultiplier", null, false));
    this._tvdCostAssumptionsService.updateComponentData(this.pestTVDMetricsData?.generalContractorCostAvoidanceComponent, eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,this.modifierForm);
    this._tvdCostAssumptionsService.updateComponentData(this.pestTVDMetricsData?.pestPreventionFindingsComponent, eroiIds.PESTPREVENTIONFINDINGS,this.modifierForm);
    this._tvdCostAssumptionsService.updateComponentData(this.pestTVDMetricsData?.furniturePreservationAssetsComponent, eroiIds.FURNITUREPRESERVATIONASSETS,this.modifierForm,[], furnitureAssetsMultiplier);

    //Update Data for two factor metrics for Room Nights Saved Metric.
    const roomNightsTwoFactorIds= this.getTwoFactorMetricIdsByMetricId('roomprice');
    this._tvdCostAssumptionsService.updateComponentDataForTwoFactorMetrics(this.pestTVDMetricsData?.roomNightsSavedComponent,eroiIds.ROOMNIGHTSSAVED,this.modifierForm,roomNightsTwoFactorIds);

    await this.getPestMetricsData(actionType);
  }

  /**
   * Retrieves the pest component associated with the given ID.
   * Throws an error if the ID is invalid.
   */
  private getPestComponentById(id: string) {
    switch (id) {
      case 'no_charge_additional_services':
        return this.pestTVDMetricsData?.noChargeAdditionalServicesComponent;
      case 'setup_services':
        return this.pestTVDMetricsData?.setupServicesComponent;
      case 'furniture_preservation_energy':
        return this.pestTVDMetricsData?.furniturePreservationEnergyComponent;
      default:
        throw Error("Invalid service metric value: " + id);
    }
  }

  public subscribeToFilters(): void {
    this.filtersSubscription = this._filterService.selectedFiltersObservable.subscribe((filters) => {
      this.filters = filters;
    });
  }

  private initializeDivisionConfig(): void {
    const division = this.filters?.division?.toLowerCase();
    let metricsConfigType: string | undefined = division;

    if (this.filters?.regions?.length === 1) {
      if (this.filters.regions[0] === 'EU') metricsConfigType = 'eu';
      else if (this.filters.regions[0] === 'AP') metricsConfigType = 'ap';
      else if (this.filters.regions[0] === 'LA') metricsConfigType = 'la';
      else if (this.filters.regions[0] === 'IMEA') metricsConfigType = 'imea';
    }

    const config = division
      ? this.divisionConfigMap.get(metricsConfigType ?? division) ?? TvdNavbarMetricsConfig
      : TvdNavbarMetricsConfig;
    this.baseMetricsDisplayConfig = this.metricsConfig = config;
  }

  //When the user clicks the save button without making any changes to the form,
  // an error/warning message should be displayed. As soon as the user enters a
  // value in the text box, the warning message should be hidden.
  public handleFormValueChanges() {
    this.modifierForm.valueChanges.subscribe(() => {
      if (this.showWarning)
        this.showWarning = !this.showWarning;
    });
  }

  /**
   * Recursively update the Form data containing the cost assumptions.
   * @param formData 
   * @param formGroup 
   * @param transform The function to apply to each numeric value in the form data.
   */
  private updateFormValues(
    formData: ModifiedFormData,
    formGroup: FormGroup,
    transform: (value: number, key: string) => number
  ): void {
    Object.entries(formData as Record<string, unknown>).forEach(([key, value]) => {
      const control = formGroup.get(key);

      //If country based cost assumptions, recursively update the nested form group
      if (control instanceof FormGroup && value !== null && typeof value === 'object' && !Array.isArray(value)) {
        this.updateFormValues(value as ModifiedFormData, control, transform);
        return;
      }

      // If value is blank, skip updating this control
      if (!control || value === null || value === undefined || value === '') {
        return;
      }

      // Convert the value to a number and apply the transformation function
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        control.setValue(transform(numericValue, key).toString());
      }
    });
  }

  loadSavedFormData() {
    const savedFormData: ModifiedFormData = this._tvdService.getFormData();
    if (Object.keys(savedFormData).length) {
      if (this.layout === 'byCountry') {
        Object.entries(savedFormData).forEach(([countryCode, countryRates]) => {
          const countryGroup = this.modifierForm.get(countryCode) as FormGroup | null;
          if (countryGroup && countryRates && typeof countryRates === 'object') {
            countryGroup.patchValue(countryRates);
          }
        });
        return;
      }

      this.modifierForm.patchValue(savedFormData);
    }
  }

  private async fetchTvdRateDetails() {
    try {
      if (this.filters?.division && this.filters.regions) {
        this._tvdService.tvdRateDetailsData$(this.filters.division, this.filters.regions).subscribe({
          next: (data) => {
            this.tvdRateDetailsResponse = data;
            this.isLoading = false;
          },
          error: (err) => {
            console.error('Error fetching TVD Rate Details:', err);
            this.isLoading = false;
          }
        });
      }
    } catch (error) {
      console.error('Unexpected error occurred while fetching TVD Rate Details:', error);
      this.isLoading = false;
    }
  }

  getCurrencyCode() {
    return this._currencyUomService.getCurrencyCode();
  }

  private mapMetricForPreview(
    metric?: TvdMetricData
  ): tvdData | undefined {

    if (!metric) {
      return undefined;
    }

    return {
      // Preview expects adjusted value here
      monetaryContribution:
        metric.modifiedMonetaryContribution,

      // Preview expects default value here
      modifiedMonetaryContribution:
        metric.monetaryContribution,

      valueContribution:
        metric.modifiedValueContribution ||
        metric.valueContribution,

      modifiedValueContribution:
        metric.valueContribution
    };
  }

  private buildPreviewState(previewData: TVDModel | undefined): TvdDialogInputState {
    const groupId = this._tvdService.getGroupId();
    const group = previewData?.tvdResponse?.find(g => g.id === groupId);

    const productivity = group?.indvMetrics?.find(m => m.id === eroiIds.PRODUCTIVITY);
    const energy = group?.indvMetrics?.find(m => m.id === eroiIds.ENERGY);
    const water = group?.indvMetrics?.find(m => m.id === eroiIds.WATER);

    const noChargeAdditionalServices = group?.indvMetrics?.find(m => m.id === eroiIds.NOCHARGEADDITIONALSERVICES);
    const generalContractorCostAvoidance = group?.indvMetrics?.find(m => m.id === eroiIds.GENERALCONTRACTORCOSTAVOIDANCE);
    const setupServices = group?.indvMetrics?.find(m => m.id === eroiIds.SETUPSERVICES);
    const pestPreventionFindings = group?.indvMetrics?.find(m => m.id === eroiIds.PESTPREVENTIONFINDINGS);
    const roomNightsSaved = group?.indvMetrics?.find(m => m.id === eroiIds.ROOMNIGHTSSAVED);
    const furniturePreservationAssets = group?.indvMetrics?.find(m => m.id === eroiIds.FURNITUREPRESERVATIONASSETS);
    const furniturePreservationEnergy = group?.indvMetrics?.find(m => m.id === eroiIds.FURNITUREPRESERVATIONENERGY);

    const toPreviewMetric = (metric?: TvdMetricData): tvdData | undefined => {

      if (!metric) {
        return undefined;
      }

      return {
        monetaryContribution: metric.monetaryContribution,
        modifiedMonetaryContribution: metric.modifiedMonetaryContribution,
        valueContribution: metric.valueContribution,
        modifiedValueContribution: metric.modifiedValueContribution
      };
    };

    const selectedMetric = this.isInDrillDown ? group?.indvMetrics?.find(
      m => m.id === this.drilldownComponentTitle?.toLowerCase()
    ) : undefined;

    const previewState: TvdDialogInputState = {
      previewData: {
        productivityData: toPreviewMetric(productivity),
        energyData: toPreviewMetric(energy),
        waterData: toPreviewMetric(water),

        pestData: this.isPestDivision
          ? {
              noChargeAdditionalServicesData: toPreviewMetric(noChargeAdditionalServices),
              generalContractorCostAvoidance: toPreviewMetric(generalContractorCostAvoidance),
              setupServices: toPreviewMetric(setupServices),
              pestPreventionFindings: toPreviewMetric(pestPreventionFindings),
              roomNightsSaved: toPreviewMetric(roomNightsSaved),
              furniturePreservationAssets: toPreviewMetric(furniturePreservationAssets),
              furniturePreservationEnergy: toPreviewMetric(furniturePreservationEnergy)
            }
          : undefined,

        isPestDivision: this.isPestDivision,
        tvdProgramScope: this.selectedProgram
      },

      drilldownSelectedMetric: selectedMetric,

      // TODO: In case the metric ID is needed in a future refactoring. But the ideal solution is pass the actual ID instead of deriving it from the title.
      // metricId: this.drilldownComponentTitle?.toLowerCase(),

      isDrillDownModal: this.isInDrillDown,

      formData: {
        ...this.modifierForm.value
      },

      selectedProgram: this.selectedProgram,

      // locationDetails: this.tvdLocationDetailsData,

      // productDetails: this.tvdProductDetailsData,

      // pestLocationDetails:
      //   this.tvdLocationServicesData,

      // tvdRateDetailsResponse:
      //   this.tvdRateDetailsResponse
    };

    return previewState;
  }
}
